import { ColorMaterialProperty, Math as CesiumMath, Quaternion, HeadingPitchRoll, Color, } from "cesium";
import * as CesiumSensorVolumes from "cesium-sensors-es6";
const DEFAULT_ALPHA = 0.2;
export var SensorType;
(function (SensorType) {
    SensorType["INTERFEROMETRY"] = "INTERFEROMETRY";
    SensorType["LASER"] = "LASER";
    SensorType["PHASED_ARRAY"] = "PHASED ARRAY";
    SensorType["RADAR"] = "RADAR";
    SensorType["RADIO_TELESCOPE"] = "RADIO TELESCOPE";
    SensorType["TELESCOPE"] = "TELESCOPE";
})(SensorType || (SensorType = {}));
export var SensorShape;
(function (SensorShape) {
    SensorShape["circular"] = "conicSensor";
    SensorShape["rectangular"] = "rectangularSensor";
})(SensorShape || (SensorShape = {}));
export const getFixedSensorRotation = (expectedRotation) => {
    if (!expectedRotation) {
        return Quaternion.fromHeadingPitchRoll(new HeadingPitchRoll(0, 0, 0), new Quaternion());
    }
    return Quaternion.fromHeadingPitchRoll(new HeadingPitchRoll(-expectedRotation.roll, -expectedRotation.heading, expectedRotation.pitch), new Quaternion());
};
export const getSensorAttitude = (parentOrientation, sensorOrientationValues, sensorFovRotationValues) => {
    const [ox, oy, oz] = sensorOrientationValues;
    const [fovAzi, fovEle, fovRot] = sensorFovRotationValues;
    const sensorOrientation = Quaternion.fromHeadingPitchRoll(HeadingPitchRoll.fromDegrees(oz, oy, ox), new Quaternion());
    const sensorRotation = Quaternion.fromHeadingPitchRoll(HeadingPitchRoll.fromDegrees(-fovRot, -fovAzi, fovEle), new Quaternion());
    const combinedOrientation = Quaternion.multiply(parentOrientation, sensorOrientation, new Quaternion());
    return Quaternion.multiply(combinedOrientation, sensorRotation, new Quaternion());
};
export const createSensorVolume = (sensorOptions, shape, xAperture, yAperture, maxRange, sensorColor, intersectionColor) => {
    let sensor3D;
    const sensorMaterial = new ColorMaterialProperty(sensorColor);
    const defaultSensorProperties = {
        show: true,
        showIntersection: true,
        intersectionColor: intersectionColor ? intersectionColor : Color.WHEAT,
        intersectionWidth: 10,
        lateralSurfaceMaterial: sensorMaterial,
        showDomeSurfaces: true,
        domeSurfaceMaterial: sensorMaterial,
        ellipsoidSurfaceMaterial: sensorMaterial,
        ellipsoidHorizonSurfaceMaterial: sensorMaterial,
        environmentOcclusionMaterial: sensorColor,
        environmentIntersectionColor: sensorColor,
        radius: maxRange,
    };
    if (shape === SensorShape.rectangular) {
        sensor3D = new CesiumSensorVolumes.RectangularSensorGraphics();
        const rectangularSensorProperties = Object.assign(Object.assign({}, defaultSensorProperties), { xHalfAngle: CesiumMath.toRadians(yAperture > 90 ? 90 : yAperture), yHalfAngle: CesiumMath.toRadians(xAperture > 90 ? 90 : xAperture) });
        Object.keys(rectangularSensorProperties).forEach((key) => {
            sensor3D[key] = rectangularSensorProperties[key];
        });
    }
    else if (shape === SensorShape.circular) {
        const circularSensorProperties = Object.assign(Object.assign({}, defaultSensorProperties), { outerHalfAngle: CesiumMath.toRadians(xAperture > 90 ? 90 : xAperture) });
        sensor3D = new CesiumSensorVolumes.ConicSensorGraphics();
        Object.keys(circularSensorProperties).forEach((key) => {
            sensor3D[key] = circularSensorProperties[key];
        });
    }
    return Object.assign(Object.assign({}, sensorOptions), { [shape]: sensor3D });
};
export const getShadowAxes = (viewer, xAperture, yAperture, maxRange) => {
    const earthRadius = viewer.scene.globe.ellipsoid.maximumRadius;
    const semiMinorLength = Math.sqrt(Math.pow(earthRadius, 2) +
        Math.pow(maxRange, 2) +
        2.0 * earthRadius * maxRange * Math.cos(CesiumMath.toRadians(xAperture)));
    const semiMinorAngle = Math.acos((Math.pow(semiMinorLength, 2) +
        Math.pow(earthRadius, 2) -
        Math.pow(maxRange, 2)) /
        (2.0 * semiMinorLength * earthRadius));
    const semiMinorAxis = earthRadius * semiMinorAngle;
    const semiMajorLength = Math.sqrt(Math.pow(earthRadius, 2) +
        Math.pow(maxRange, 2) +
        2 * earthRadius * maxRange * Math.cos(CesiumMath.toRadians(yAperture)));
    const semiMajorAngle = Math.acos((Math.pow(semiMajorLength, 2) +
        Math.pow(earthRadius, 2) -
        Math.pow(maxRange, 2)) /
        (2.0 * semiMajorLength * earthRadius));
    const semiMajorAxis = earthRadius * semiMajorAngle;
    return {
        semiMajorAxis,
        semiMinorAxis,
    };
};
export const isRectangularSensor = (sensor) => {
    return sensor.rectangularSensor !== undefined;
};
export const getViewOptions = (parentName, entity, viewer, canvasRef) => {
    const initiator = () => {
        const ratio = canvasRef.current.width / canvasRef.current.height;
        if (isRectangularSensor(entity)) {
            const angle = entity.rectangularSensor.yHalfAngle.getValue() >
                entity.rectangularSensor.xHalfAngle.getValue()
                ? 2 * entity.rectangularSensor.yHalfAngle.getValue()
                : 2 * entity.rectangularSensor.xHalfAngle.getValue();
            viewer.camera.frustum.fov =
                ratio > 0 ? angle * ratio : angle;
        }
        else {
            const angle = 2 * entity.conicSensor.outerHalfAngle.getValue();
            viewer.camera.frustum.fov =
                ratio > 0 ? angle * ratio : angle;
        }
    };
    const finisher = () => { };
    return {
        id: `${entity.id}_${parentName}`,
        name: entity.id,
        initiator,
        finisher,
    };
};
export const getSensorDescription = (sensor) => {
    return `
        <table style="width:100%">
        <tr>
          <th>Key</th>
          <th>Value</th>
        </tr>
        <tr>
          <td><strong>Name</strong></td>
          <td>${sensor.sensorName}</td>
        </tr>
        <tr>
        <td><strong>Country</strong></td>
        <td>${sensor.sensorName.split("_")[0]}</td>
      </tr>
      </table>
      `;
};
export const getSensorColor = (sensorType) => {
    switch (sensorType) {
        case SensorType.INTERFEROMETRY:
            return new Color(0, 1, 0, DEFAULT_ALPHA);
        case SensorType.PHASED_ARRAY:
            return new Color(0.7843137254901961, 0, 0.7843137254901961, DEFAULT_ALPHA);
        case SensorType.RADAR:
            return new Color(0, 0, 1, DEFAULT_ALPHA);
        case SensorType.RADIO_TELESCOPE:
            return new Color(1, 1, 0, DEFAULT_ALPHA);
        case SensorType.TELESCOPE:
            return new Color(0.7843137254901961, 0, 0, DEFAULT_ALPHA);
        default:
            return new Color(1, 1, 1, DEFAULT_ALPHA);
    }
};
//# sourceMappingURL=sensor.js.map